import { ProductFilter, ProductsResponseOut } from '../../store/api';
import { localContentPrefix, shopPrefix } from '../../config';
import { ProductMetaData, ProductSorting } from '../../../models/Product';
import { LoanFormat } from '../../../models/LoanFormat';
import { AgeCategory } from '../../../models/AgeCategory';
import { mapProductOutToProductMetaDataArray } from '../ProductOutMappings';
import { http } from '../axios';

export type FetchProductsFromCatalogueResponse = {
    readonly total?: number;
    readonly products?: ReadonlyArray<ProductMetaData>;
};

export type FetchProductsFromCatalogueParams = {
    readonly page?: number;
    readonly pageSize?: number;
    readonly sortBy?: ProductSorting;
    readonly ageCategory?: AgeCategory;
    readonly language?: string;
    readonly shopLanguage?: string;
};

export const fetchProductsFromCatalogue = (
    loanFormat: LoanFormat,
    filterMap?: ProductFilter,
    { page, pageSize, sortBy, ageCategory, shopLanguage }: FetchProductsFromCatalogueParams = {}
): Promise<FetchProductsFromCatalogueResponse> => {
    return http
        .post<ProductsResponseOut>(`${shopPrefix}/v2/standard/allproducts/:loanFormat/products`, filterMap ?? {}, {
            pathParams: { loanFormat: loanFormat.toUpperCase() },
            params: {
                page,
                pageSize,
                sortBy,
                ageCategory,
                shopLanguage
            },
            requestGroup: [
                loanFormat,
                JSON.stringify(filterMap),
                page,
                pageSize,
                sortBy,
                ageCategory,
                shopLanguage
            ].join('-')
        })
        .then(response => ({
            total: response.data.totalHits ?? 0,
            products: mapProductOutToProductMetaDataArray(response.data.hits)
        }));
};

export const fetchProductsFromLote = (
    loanFormat: LoanFormat,
    filterMap?: ProductFilter,
    { page, pageSize, sortBy, ageCategory, language }: FetchProductsFromCatalogueParams = {}
): Promise<FetchProductsFromCatalogueResponse> => {
    return http
        .post<ProductsResponseOut>(`${shopPrefix}/v1/lote/allproducts/products`, filterMap, {
            params: {
                page,
                pageSize,
                sortBy,
                ageCategory,
                language,
                loanFormat
            },
            requestGroup: [loanFormat, JSON.stringify(filterMap), page, pageSize, sortBy, ageCategory].join('-')
        })
        .then(response => ({
            total: response.data.totalHits ?? 0,
            products: mapProductOutToProductMetaDataArray(response.data.hits)
        }));
};

export const fetchProductsFromLanguageLearning = (
    loanFormat: LoanFormat,
    filterMap?: ProductFilter,
    { page, pageSize, sortBy, ageCategory, language, shopLanguage }: FetchProductsFromCatalogueParams = {}
): Promise<FetchProductsFromCatalogueResponse> => {
    return http
        .post<ProductsResponseOut>(`${shopPrefix}/v1/languagelearning/allproducts/products`, filterMap, {
            params: {
                page,
                pageSize,
                sortBy,
                ageCategory,
                language,
                loanFormat: loanFormat ? loanFormat.toUpperCase() : undefined,
                shopLanguage
            },
            requestGroup: [loanFormat, JSON.stringify(filterMap), page, pageSize, sortBy, ageCategory].join('-')
        })
        .then(response => ({
            total: response.data.totalHits ?? 0,
            products: mapProductOutToProductMetaDataArray(response.data.hits)
        }));
};

export const fetchLibxProductsFromCatalogue = (
    loanFormat: LoanFormat,
    filterMap?: ProductFilter,
    { page, pageSize, sortBy, ageCategory, shopLanguage }: FetchProductsFromCatalogueParams = {}
): Promise<FetchProductsFromCatalogueResponse> => {
    return http
        .post<ProductsResponseOut>(`${localContentPrefix}/v1/marketplace/list/products`, filterMap, {
            params: {
                page,
                pageSize,
                sortBy,
                ageCategory,
                loanFormat: loanFormat ? loanFormat.toUpperCase() : undefined,
                shopLanguage
            },
            requestGroup: [loanFormat, JSON.stringify(filterMap), page, pageSize, sortBy, ageCategory].join('-')
        })
        .then(response => ({
            total: response.data.totalHits ?? 0,
            products: mapProductOutToProductMetaDataArray(response.data.hits)
        }));
};
